import {
  applyBondingCurveTradeToMarketPoolSnapshot,
  applyCpPoolStateToMarketPoolSnapshot,
  type MarketPoolRealtimeSnapshot as MarketPoolSnapshot,
} from '@morfi/contract-sdk';
import { isU64, type MarketPoolQuoteSnapshot, reserveRatioSpotPriceD9 } from '@morfi/market-math';
import {
  decodeProfilePortfolioPoolEvent,
  type ProfilePortfolioPoolEvent,
} from '../../../nats/profile-portfolio-event.js';
import { base64ToCursor, compareCursor, cursorToBase64 } from '../../../nats/sequence-id.js';
import type {
  UserPortfolioPoolEntryWire,
  UserPortfolioPoolIdentityWire,
  UserPortfolioSnapshotWire,
} from './types';

export type ApplyUserPortfolioSnapshotPoolEventResult =
  | { readonly status: 'applied'; readonly snapshot: UserPortfolioSnapshotWire }
  | { readonly status: 'ignored' }
  | {
      readonly status: 'reconciliation_required';
      readonly reason:
        | 'event_identity_invalid'
        | 'event_payload_incomplete'
        | 'represented_wallet_event'
        | 'sequence_gap';
    };

export function applyUserPortfolioSnapshotPoolEvent(
  snapshot: UserPortfolioSnapshotWire,
  representedWallets: readonly string[],
  subject: string,
  payload: Uint8Array,
): ApplyUserPortfolioSnapshotPoolEventResult {
  const decoded = decodeProfilePortfolioPoolEvent(subject, payload);
  if (decoded.status === 'reconciliation_required') {
    return {
      status: 'reconciliation_required',
      reason:
        decoded.reason === 'identity_invalid'
          ? 'event_identity_invalid'
          : 'event_payload_incomplete',
    };
  }
  return applyDecodedUserPortfolioSnapshotPoolEvent(snapshot, representedWallets, decoded.event);
}

export function applyDecodedUserPortfolioSnapshotPoolEvent(
  snapshot: UserPortfolioSnapshotWire,
  representedWallets: readonly string[],
  event: ProfilePortfolioPoolEvent,
): ApplyUserPortfolioSnapshotPoolEventResult {
  if (representedWallets.includes(event.actorWallet)) {
    return { status: 'reconciliation_required', reason: 'represented_wallet_event' };
  }
  const gate = eventSequenceGate(snapshot, event);
  if (gate !== 'newer') {
    return gate === 'older_or_equal'
      ? { status: 'ignored' }
      : { status: 'reconciliation_required', reason: 'sequence_gap' };
  }
  const poolEntry = snapshot.poolSnapshotsByMarket[event.market];
  if (poolEntry === undefined) {
    return { status: 'reconciliation_required', reason: 'event_identity_invalid' };
  }
  if (poolEntry.status !== 'complete') {
    return { status: 'reconciliation_required', reason: 'event_payload_incomplete' };
  }
  if (event.venue === 'cp_swap' && event.updateKind === 'reconciliation_required') {
    return { status: 'reconciliation_required', reason: 'event_payload_incomplete' };
  }
  const patchedPool = patchPoolSnapshot(poolEntry, event);
  if (patchedPool.status === 'reconciliation_required') return patchedPool;

  const sequence = cursorToBase64(event.sequenceId);
  if (sequence === null) {
    return { status: 'reconciliation_required', reason: 'sequence_gap' };
  }
  const positions = applyPostOutcomePrice(snapshot.positions, event);
  return {
    status: 'applied',
    snapshot: {
      ...snapshot,
      positions,
      poolSnapshotsByMarket: {
        ...snapshot.poolSnapshotsByMarket,
        [event.market]: { ...poolEntry, snapshot: patchedPool.snapshot },
      },
      recovery: {
        ...snapshot.recovery,
        marketSequences: {
          ...snapshot.recovery.marketSequences,
          [event.market]: sequence,
        },
        outcomeSequences:
          event.venue === 'cp_swap'
            ? { ...snapshot.recovery.outcomeSequences, [event.outcomeMint]: sequence }
            : snapshot.recovery.outcomeSequences,
      },
    },
  };
}

function applyPostOutcomePrice(
  positions: UserPortfolioSnapshotWire['positions'],
  event: ProfilePortfolioPoolEvent,
): UserPortfolioSnapshotWire['positions'] {
  const currentPriceD9 = event.currentPriceD9.toString();
  let changed = false;
  const next = positions.map((position) => {
    if (
      position.market !== event.market ||
      position.outcomeIndex !== event.outcomeIndex ||
      position.outcomeMint !== event.outcomeMint ||
      position.currentPriceD9 === currentPriceD9
    ) {
      return position;
    }
    changed = true;
    return { ...position, currentPriceD9 };
  });
  return changed ? next : positions;
}

type PatchPoolSnapshotResult =
  | { readonly status: 'applied'; readonly snapshot: MarketPoolQuoteSnapshot }
  | {
      readonly status: 'reconciliation_required';
      readonly reason: 'event_identity_invalid' | 'event_payload_incomplete';
    };

function patchPoolSnapshot(
  entry: Extract<UserPortfolioPoolEntryWire, { status: 'complete' }>,
  event: ProfilePortfolioPoolEvent,
): PatchPoolSnapshotResult {
  const executable = executableSnapshot(entry.snapshot, entry.identity);
  if (executable === null) {
    return { status: 'reconciliation_required', reason: 'event_payload_incomplete' };
  }
  if (event.venue === 'bonding_curve') {
    const patched = applyBondingCurveTradeToMarketPoolSnapshot(executable, event);
    if (!patched.ok) return poolPatchFailure(patched.reason);
    const outcome = patched.snapshot.outcomes.find(
      (candidate) => candidate.outcomeIndex === event.outcomeIndex,
    );
    const curve = outcome?.bondingCurve;
    if (curve === null || curve === undefined) {
      return { status: 'reconciliation_required', reason: 'event_payload_incomplete' };
    }
    const exactPrice = reserveRatioSpotPriceD9(
      BigInt(curve.poolQuoteReserveD6),
      BigInt(curve.poolOutcomeReserveD6),
    );
    if (!exactPrice.available || exactPrice.value !== event.currentPriceD9) {
      return { status: 'reconciliation_required', reason: 'event_payload_incomplete' };
    }
    return { status: 'applied', snapshot: quoteSnapshot(patched.snapshot) };
  }
  if (
    entry.identity.poolId !== event.poolId ||
    entry.identity.outcomeMint !== event.outcomeMint ||
    event.market !== entry.snapshot.market
  ) {
    return { status: 'reconciliation_required', reason: 'event_identity_invalid' };
  }
  const patched = applyCpPoolStateToMarketPoolSnapshot(executable, {
    pool: event.poolId,
    outcomeMint: event.outcomeMint,
    chainSlot: event.chainSlot,
    chainUnixTimestampSeconds: event.chainUnixTimestampSeconds,
    postQuoteReserveD6: event.postQuoteReserveD6,
    postOutcomeReserveD6: event.postOutcomeReserveD6,
    postOutcomePriceD9: event.currentPriceD9,
  });
  return patched.ok
    ? { status: 'applied', snapshot: quoteSnapshot(patched.snapshot) }
    : poolPatchFailure(patched.reason);
}

function executableSnapshot(
  snapshot: MarketPoolQuoteSnapshot,
  identity: UserPortfolioPoolIdentityWire,
): MarketPoolSnapshot | null {
  const outcomes: Array<MarketPoolSnapshot['outcomes'][number]> = [];
  for (const outcome of snapshot.outcomes) {
    let bondingCurve: MarketPoolSnapshot['outcomes'][number]['bondingCurve'] = null;
    if (outcome.bondingCurve !== null) {
      const initial = unsignedWire(outcome.bondingCurve.initialOutcomeSupplyD6);
      const reserve = unsignedWire(outcome.bondingCurve.poolOutcomeReserveD6);
      if (initial === null || reserve === null || reserve > initial) return null;
      bondingCurve = {
        ...outcome.bondingCurve,
        trackedBondingCurveSoldSupplyD6: (initial - reserve).toString(),
      };
    }
    let cpSwap: MarketPoolSnapshot['outcomes'][number]['cpSwap'] = null;
    if (outcome.cpSwap !== null) {
      if (identity.poolId === null) return null;
      cpSwap = { ...outcome.cpSwap, pool: identity.poolId };
    }
    outcomes.push({ ...outcome, bondingCurve, cpSwap });
  }
  const gate = snapshot.bondingCurveTradeGate;
  return {
    ...snapshot,
    bondingCurveTradeGate:
      gate === null
        ? null
        : {
            ...gate,
            outcomeConvictionPpb: [...gate.outcomeConvictionPpb],
          },
    outcomes,
  };
}

function quoteSnapshot(snapshot: MarketPoolSnapshot): MarketPoolQuoteSnapshot {
  return {
    ...snapshot,
    outcomes: snapshot.outcomes.map((outcome) => ({
      ...outcome,
      bondingCurve:
        outcome.bondingCurve === null ? null : withoutTrackedSupply(outcome.bondingCurve),
      cpSwap: outcome.cpSwap === null ? null : withoutPoolIdentity(outcome.cpSwap),
    })),
  };
}

function withoutTrackedSupply(
  curve: NonNullable<MarketPoolSnapshot['outcomes'][number]['bondingCurve']>,
): NonNullable<MarketPoolQuoteSnapshot['outcomes'][number]['bondingCurve']> {
  const { trackedBondingCurveSoldSupplyD6: _tracked, ...quoteCurve } = curve;
  return quoteCurve;
}

function withoutPoolIdentity(
  pool: NonNullable<MarketPoolSnapshot['outcomes'][number]['cpSwap']>,
): NonNullable<MarketPoolQuoteSnapshot['outcomes'][number]['cpSwap']> {
  const { pool: _pool, ...quotePool } = pool;
  return quotePool;
}

function poolPatchFailure(
  reason:
    | 'identity_mismatch'
    | 'lifecycle_mismatch'
    | 'missing_pool_state'
    | 'malformed_event'
    | 'non_monotonic_event'
    | 'incoherent_post_state',
): PatchPoolSnapshotResult {
  return {
    status: 'reconciliation_required',
    reason: reason === 'identity_mismatch' ? 'event_identity_invalid' : 'event_payload_incomplete',
  };
}

function eventSequenceGate(
  snapshot: UserPortfolioSnapshotWire,
  event: ProfilePortfolioPoolEvent,
): 'newer' | 'older_or_equal' | 'invalid_watermark' {
  const encoded =
    event.venue === 'cp_swap'
      ? (snapshot.recovery.outcomeSequences[event.outcomeMint] ??
        snapshot.recovery.marketSequences[event.market])
      : snapshot.recovery.marketSequences[event.market];
  if (encoded === undefined) return 'newer';
  const watermark = base64ToCursor(encoded);
  if (watermark === null) return 'invalid_watermark';
  return compareCursor(event.sequenceId, watermark) > 0 ? 'newer' : 'older_or_equal';
}

function unsignedWire(value: string): bigint | null {
  if (!/^(?:0|[1-9]\d*)$/.test(value)) return null;
  const parsed = BigInt(value);
  return isU64(parsed) ? parsed : null;
}
